import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ContentType } from "@/types/content";
import { useToast } from "@/hooks/use-toast";
import { incrementUsage } from "@/hooks/useUsageLimits";
import { trackEvent } from "@/lib/analytics";

export const useContentUpload = () => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const getContentType = (file: File): ContentType => {
    const mimeType = file.type;
    const name = file.name.toLowerCase();

    if (mimeType.includes("pdf")) return "pdf";
    if (
      mimeType === "application/vnd.ms-powerpoint" ||
      mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
      name.endsWith(".ppt") ||
      name.endsWith(".pptx")
    ) return "ppt";
    if (mimeType.includes("msword") || mimeType.includes("wordprocessingml")) return "doc";
    if (mimeType.startsWith("audio/")) return "audio";
    if (mimeType.startsWith("video/")) return "video";

    return "doc";
  };

  const uploadContent = async (file: File, roomId?: string) => {
    setUploading(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Determine content type
      const contentType = getContentType(file);
      
      // Upload file to storage
      const fileName = `${user.id}/${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("content-uploads")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get storage path
      const { data: { publicUrl } } = supabase.storage
        .from("content-uploads")
        .getPublicUrl(fileName);

      // Create content record
      const { data: content, error: contentError } = await supabase
        .from("content")
        .insert({
          user_id: user.id,
          room_id: roomId || null,
          title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
          type: contentType,
          storage_path: fileName,
          source_url: publicUrl,
          visibility: "private",
          // Set conversion status based on content type
          conversion_status: contentType === "pdf" ? "not_needed" : "pending",
          pdf_storage_path: contentType === "pdf" ? fileName : null,
        })
        .select()
        .single();

      if (contentError) throw contentError;

      // Track usage for content uploads
      await incrementUsage("content_uploads");
      trackEvent("content_created", { content_id: content.id, content_type: contentType });
      if (contentType === "doc" || contentType === "text" || contentType === "ppt") {
        supabase.functions
          .invoke("convert-content-to-pdf", {
            body: {
              contentId: content.id,
              type: contentType,
              storagePath: fileName,
            },
          })
          .then(({ data, error }) => {
            if (error) throw error;
            if (data && data.success === false) {
              toast({
                title: "Conversion unavailable",
                description: data.error || "We couldn't convert this file right now.",
                variant: "destructive",
              });
            }
          })
          .catch((err) => console.error("PDF conversion failed:", err));
      }

      // Automatically trigger transcript generation for audio/video
      if (contentType === "audio" || contentType === "video") {
        supabase.functions
          .invoke("extract-media-transcript", {
            body: { contentId: content.id },
          })
          .catch((err) => console.error("Transcript generation failed:", err));
      }

      return content;
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload content",
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  return { uploadContent, uploading };
};
